import { Inflate } from 'pako';
import { normalizeGraph } from './graph.js';
import { anchorAt, edgeGeometry } from './geometry.js';
import { getPaintOrder, MAX_Z_INDEX } from './layers.js';
import { getNodeAppearance } from './appearance.js';

const LIMIT = 2 * 1024 * 1024;
const encoder = new TextEncoder();
const escapeXml = value => String(value ?? '').replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' }[char])).replace(/\r/g, '&#13;').replace(/\n/g, '&#10;').replace(/\t/g, '&#9;');
const elements = node => Array.from(node.childNodes || []).filter(child => child.nodeType === 1);
const attr = (node, name, fallback = '') => node?.hasAttribute(name) ? node.getAttribute(name) : fallback;
const number = (node, name, fallback = 0) => {
  const value = attr(node, name, '');
  if (value === '') return fallback;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || Math.abs(parsed) > 10000000) throw new Error('draw.io 文件中有无效或过大的几何数值。');
  return parsed;
};
const hex = value => /^#[\da-f]{3}(?:[\da-f]{3})?$/i.test(value || '') ? value : null;
const appearanceKeys = ['strokeWidth', 'borderStyle', 'headerStyle', 'radius', 'fillColor', 'strokeColor', 'fontColor'];

function parseXml(text, Parser) {
  if (typeof text !== 'string' || !text.trim()) throw new Error('请选择有效的 draw.io XML 文件。');
  if (encoder.encode(text).length > LIMIT) throw new Error('draw.io 文件或解压后的页面不能超过 2 MB。');
  if (/<!\s*(?:DOCTYPE|ENTITY)\b/i.test(text)) throw new Error('不支持包含外部实体或文档类型声明的 XML。');
  if (!Parser) throw new Error('当前环境没有 XML 解析器。');
  const errors = [];
  let doc;
  try {
    doc = new Parser({ onError(level, message) { if (level !== 'warning') errors.push(message); } }).parseFromString(text, 'application/xml');
  } catch { throw new Error('draw.io XML 格式有误，无法读取。'); }
  if (!doc?.documentElement || errors.length || doc.getElementsByTagName('parsererror').length) throw new Error('draw.io XML 格式有误，无法读取。');
  return doc;
}

function pagesFromDocument(doc) {
  const root = doc.documentElement;
  if (root.nodeName === 'mxGraphModel') return [{ id: 'page-1', name: 'Page-1', index: 0, element: root }];
  const diagrams = root.nodeName === 'diagram' ? [root] : root.nodeName === 'mxfile' ? elements(root).filter(node => node.nodeName === 'diagram') : [];
  if (!diagrams.length) throw new Error('文件中没有 draw.io 图页面。');
  return diagrams.map((element, index) => ({ id: attr(element, 'id', `page-${index + 1}`), name: attr(element, 'name', `Page-${index + 1}`), index, element }));
}

export function listDrawioPages(xml, { DOMParser: Parser = globalThis.DOMParser } = {}) {
  return pagesFromDocument(parseXml(xml, Parser)).map(({ id, name, index }) => ({ id, name, index }));
}

function decodePage(page, Parser) {
  if (page.element.nodeName === 'mxGraphModel') return page.element;
  const model = elements(page.element).find(node => node.nodeName === 'mxGraphModel');
  if (model) return model;
  let text = page.element.textContent.trim();
  if (!text.startsWith('<')) {
    try {
      const binary = globalThis.atob(text.replace(/\s/g, ''));
      const bytes = Uint8Array.from(binary, char => char.charCodeAt(0));
      const inflate = new Inflate({ raw: true, to: 'string', chunkSize: 16384 });
      let length = 0;
      const parts = [];
      inflate.onData = chunk => {
        length += encoder.encode(chunk).length;
        if (length > LIMIT) throw new Error('页面解压后超过 2 MB。');
        parts.push(chunk);
      };
      inflate.push(bytes, true);
      if (inflate.err) throw new Error(inflate.msg);
      text = parts.join('');
      if (!text.startsWith('<')) text = decodeURIComponent(text);
    } catch (error) {
      if (error.message.includes('2 MB')) throw error;
      throw new Error('draw.io 压缩页面损坏或编码不受支持。');
    }
  }
  const decoded = parseXml(text, Parser).documentElement;
  if (decoded.nodeName !== 'mxGraphModel') throw new Error('页面中没有有效的图模型。');
  return decoded;
}

function styleMap(raw) {
  const result = Object.create(null);
  String(raw || '').split(';').filter(Boolean).forEach(part => {
    const position = part.indexOf('=');
    if (position === -1) result[part] = '1';
    else result[part.slice(0, position)] = part.slice(position + 1);
  });
  return result;
}

function layerIndex(record) {
  if (!record.cell.hasAttribute('regionZIndex')) return record.ordinal;
  const raw = attr(record.cell, 'regionZIndex');
  const value = Number(raw);
  if (!raw.trim() || !Number.isInteger(value) || Math.abs(value) > MAX_Z_INDEX) throw new Error('draw.io 图层顺序无效。');
  return value;
}

function iconAsset(cell) {
  if (!cell.hasAttribute('regionIconAsset')) return null;
  let asset;
  try { asset = JSON.parse(attr(cell, 'regionIconAsset')); } catch { throw new Error('draw.io 自定义图标资源损坏。'); }
  if (!asset || typeof asset !== 'object' || Array.isArray(asset)) throw new Error('draw.io 自定义图标资源无效。');
  // normalizeGraph applies the shared identity, author and inline-content checks.
  return asset;
}

function imageStyleData(dataUrl) {
  const [header, content] = dataUrl.split(',');
  const mimeType = header.slice(5, -7);
  // A style value cannot contain ";", including the one in ";base64". A
  // percent-encoded data URI is standard in both draw.io and browser images.
  const encoded = Array.from(atob(content), character => /[A-Za-z0-9_.~-]/.test(character) ? character : `%${character.charCodeAt(0).toString(16).padStart(2, '0').toUpperCase()}`).join('');
  return `data:${mimeType},${encoded}`;
}

function nodeAppearance(record, width, height, warn) {
  const { cell, style } = record;
  if (cell.hasAttribute('regionAppearance')) {
    let value;
    try { value = JSON.parse(attr(cell, 'regionAppearance')); } catch { throw new Error('draw.io 节点样式数据损坏。'); }
    if (!value || typeof value !== 'object' || Array.isArray(value) || Object.keys(value).some(key => !appearanceKeys.includes(key))) throw new Error('draw.io 节点样式数据包含无效字段。');
    if (typeof value.strokeWidth !== 'number' || !Number.isFinite(value.strokeWidth) || value.strokeWidth < 0 || value.strokeWidth > 8 || typeof value.radius !== 'number' || !Number.isFinite(value.radius) || value.radius < 0 || value.radius > 40 || !['solid', 'dashed', 'dotted', 'none'].includes(value.borderStyle) || !['bar', 'plain'].includes(value.headerStyle)) throw new Error('draw.io 节点样式数据无效。');
    for (const key of ['fillColor', 'strokeColor', 'fontColor']) if (Object.hasOwn(value, key) && value[key] !== null && !hex(value[key])) throw new Error('draw.io 节点样式颜色无效。');
    return value;
  }
  function styleNumber(key, fallback, min, max) {
    if (style[key] === undefined) return fallback;
    const raw = Number(style[key]);
    if (!String(style[key]).trim() || !Number.isFinite(raw)) { warn('不受支持的节点样式数值已使用安全默认值。'); return fallback; }
    if (raw < min || raw > max) warn('超出编辑器范围的节点样式尺寸已适当调整。');
    return Math.min(max, Math.max(min, raw));
  }
  const strokeWidth = styleNumber('strokeWidth', 1, 0, 8);
  let borderStyle = style.strokeColor === 'none' || strokeWidth === 0 ? 'none' : style.dashed === '1' ? 'dashed' : 'solid';
  if (borderStyle === 'dashed' && style.dashPattern) {
    const pattern = style.dashPattern.trim().split(/[\s,]+/).map(Number);
    if (pattern.length >= 2 && pattern.every(value => Number.isFinite(value) && value >= 0) && pattern[0] <= 1 && pattern[1] > 0) borderStyle = 'dotted';
  }
  // mxRectangleShape interprets absolute arcSize as a diameter, while its
  // ordinary arcSize is a percentage of the shorter rectangle dimension.
  let radius = 0;
  if (style.rounded === '1') {
    const absolute = style.absoluteArcSize === '1';
    const arc = styleNumber('arcSize', absolute ? 20 : 15, 0, 10000000);
    radius = Math.min(width / 2, height / 2, absolute ? arc / 2 : Math.min(width, height) * arc / 100);
    if (radius > 40) { radius = 40; warn('超出编辑器范围的节点样式尺寸已适当调整。'); }
  }
  return {
    strokeWidth, borderStyle, radius, headerStyle: style.swimlaneLine === '0' ? 'plain' : 'bar',
    ...(hex(style.fillColor) ? { fillColor: style.fillColor } : {}),
    ...(hex(style.strokeColor) ? { strokeColor: style.strokeColor } : {}),
    ...(hex(style.fontColor) ? { fontColor: style.fontColor } : {})
  };
}

function plainLabel(value, rich) {
  let text = String(value || '');
  if (rich) {
    text = text.replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, '').replace(/<br\s*\/?\s*>/gi, '\n').replace(/<\/(div|p)>/gi, '\n').replace(/<[^>]*>/g, '');
    text = text.replace(/&(#x[\da-f]+|#\d+|amp|lt|gt|quot|apos|nbsp);/gi, (match, code) => {
      const known = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' };
      if (known[code.toLowerCase()] !== undefined) return known[code.toLowerCase()];
      const number = code[1]?.toLowerCase() === 'x' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
      return Number.isInteger(number) && number > 0 && number <= 0x10ffff ? String.fromCodePoint(number) : match;
    });
  }
  return text.trim();
}

export function parseDrawio(xml, { pageIndex = 0, DOMParser: Parser = globalThis.DOMParser } = {}) {
  const rawPages = pagesFromDocument(parseXml(xml, Parser));
  if (!Number.isInteger(pageIndex) || pageIndex < 0 || pageIndex >= rawPages.length) throw new Error('请选择有效的图页面。');
  const page = rawPages[pageIndex], model = decodePage(page, Parser);
  const warnings = [], warningSet = new Set(), warn = message => { if (!warningSet.has(message)) { warningSet.add(message); warnings.push(message); } };
  const records = [], byId = new Map();
  Array.from(model.getElementsByTagName('mxCell')).forEach((cell, index) => {
    const wrapper = cell.parentNode?.nodeName === 'root' ? null : cell.parentNode;
    const id = attr(cell, 'id', attr(wrapper, 'id', `cell-${index}`));
    if (byId.has(id)) throw new Error('draw.io 文件中有重复的图元身份。');
    const geometry = elements(cell).find(node => node.nodeName === 'mxGeometry');
    const record = { id, cell, geometry, ordinal: index, parent: attr(cell, 'parent'), vertex: attr(cell, 'vertex') === '1', edge: attr(cell, 'edge') === '1', style: styleMap(attr(cell, 'style')), value: attr(cell, 'value', attr(wrapper, 'label')) };
    records.push(record); byId.set(id, record);
  });
  if (records.length > 7000) throw new Error('draw.io 文件中的图元过多。');
  const vertices = records.filter(record => record.vertex), vertexIds = new Set(vertices.map(record => record.id));
  function vertexParent(record) {
    let id = record.parent;
    const visited = new Set([record.id]);
    while (id && byId.has(id)) {
      if (visited.has(id)) throw new Error('draw.io 文件包含循环父子关系。');
      visited.add(id);
      if (visited.size > 66) throw new Error('draw.io 文件的嵌套层级过深。');
      if (vertexIds.has(id)) return id;
      id = byId.get(id).parent;
    }
    return null;
  }
  const worldCache = new Map();
  function world(record, visited = new Set()) {
    if (worldCache.has(record.id)) return worldCache.get(record.id);
    if (visited.has(record.id) || visited.size > 65) throw new Error('draw.io 文件包含循环或过深的父子关系。');
    visited.add(record.id);
    let x = number(record.geometry, 'x'), y = number(record.geometry, 'y');
    const parent = byId.get(record.parent);
    if (parent) {
      const origin = world(parent, visited);
      if (attr(record.geometry, 'relative') === '1' && parent.vertex) {
        x *= number(parent.geometry, 'width', 160); y *= number(parent.geometry, 'height', 72);
        warn('相对定位的图元已转换为绝对位置，端口的交互行为不会保留。');
      }
      x += origin.x; y += origin.y;
    }
    const result = { x, y }; worldCache.set(record.id, result); return result;
  }
  const parents = new Map(vertices.map(record => [record.id, vertexParent(record)]));
  const mappedIds = new Map(vertices.map(record => [record.id, attr(record.cell, 'regionId', record.id)]));
  const containers = new Set([...parents.values()].filter(Boolean));
  vertices.forEach(record => { if (record.style.container === '1' || record.style.swimlane || record.style.group || record.style.shape === 'swimlane') containers.add(record.id); });
  const nodes = vertices.map(record => {
    const { cell, geometry, style } = record, container = containers.has(record.id), position = world(record), asset = iconAsset(cell);
    const label = plainLabel(record.value, style.html === '1');
    const split = label.split('\n');
    const title = attr(cell, 'regionTitle', split.shift() || (container ? '导入区域' : '导入节点'));
    const description = attr(cell, 'regionDescription', split.join('\n'));
    const shape = style.shape || (style.ellipse ? 'ellipse' : style.rhombus ? 'rhombus' : style.image ? 'image' : style.text ? 'text' : 'rectangle');
    if (!['rectangle', 'swimlane'].includes(shape) && !(shape === 'image' && asset)) warn(`形状“${shape}”已转换为基础矩形节点。`);
    if (style.html === '1') warn('富文本已转换为纯文字。');
    if ((style.image || style.shape === 'image') && !asset) warn('图片与外部图标未加载，已保留为可编辑文字节点。');
    if (geometry?.getElementsByTagName('mxPoint').length) warn('图元偏移与自定义端口已简化。');
    const rawW = number(geometry, 'width', container ? 240 : 160), rawH = number(geometry, 'height', container ? 180 : 72);
    const w = Math.max(container ? 200 : 80, rawW), h = Math.max(container ? 160 : 40, rawH);
    if (w !== rawW || h !== rawH) warn('小于编辑器最小尺寸的图元已适当放大。');
    return {
      id: mappedIds.get(record.id), parentId: mappedIds.get(parents.get(record.id)) || null, type: container ? 'container' : 'service', ...position,
      w, h, title, description, zIndex: layerIndex(record), icon: attr(cell, 'regionIcon', container ? 'layers' : 'server'),
      ...(asset ? { iconAsset: asset } : {}),
      widthMode: container ? 'auto' : 'fixed', heightMode: container ? 'auto' : 'fixed',
      minW: container ? w : 120, minH: container ? h : 72, layout: 'free', padding: 20, gap: 16,
      textAlign: ['left', 'center', 'right'].includes(style.align) ? style.align : 'left',
      overflow: style.whiteSpace === 'wrap' ? 'wrap' : 'ellipsis', iconPosition: 'left', color: attr(cell, 'regionColor', 'neutral'),
      ...nodeAppearance(record, w, h, warn)
    };
  });
  const edges = [];
  records.filter(record => record.edge).forEach(record => {
    const source = attr(record.cell, 'source'), target = attr(record.cell, 'target');
    if (!vertexIds.has(source) || !vertexIds.has(target) || source === target) { warn('无有效节点端点或连接自身的连线已跳过。'); return; }
    const geometry = {}, ownGeometry = attr(record.cell, 'regionGeometry');
    if (ownGeometry) {
      let parsed;
      try { parsed = JSON.parse(ownGeometry); } catch { throw new Error('draw.io 连线控制点数据损坏。'); }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('draw.io 连线控制点数据无效。');
      for (const key of ['sourceAnchor', 'targetAnchor', 'waypoints', 'labelOffset']) if (Object.hasOwn(parsed, key)) geometry[key] = parsed[key];
    } else {
      const parent = byId.get(record.parent), origin = parent ? world(parent) : { x: 0, y: 0 };
      const points = elements(record.geometry || {}).find(child => child.nodeName === 'Array' && attr(child, 'as') === 'points');
      if (points) geometry.waypoints = elements(points).filter(child => child.nodeName === 'mxPoint').map(child => ({ x: number(child, 'x') + origin.x, y: number(child, 'y') + origin.y }));
      else if (record.style.edgeStyle === 'none') geometry.waypoints = [];
      for (const [prefix, key, nodeId] of [['exit', 'sourceAnchor', source], ['entry', 'targetAnchor', target]]) {
        if (record.style[prefix + 'X'] === undefined || record.style[prefix + 'Y'] === undefined) continue;
        const x = Number(record.style[prefix + 'X']), y = Number(record.style[prefix + 'Y']);
        if (![x, y].every(Number.isFinite) || x < 0 || x > 1 || y < 0 || y > 1) throw new Error('draw.io 连线端口坐标无效。');
        const node = nodes.find(item => item.id === mappedIds.get(nodeId));
        geometry[key] = anchorAt(node, { x: node.x + x * node.w, y: node.y + y * node.h });
        if (x !== 0 && x !== 1 && y !== 0 && y !== 1) warn('节点内部端口已移到最近的边界，连线仍可继续调整。');
      }
      const offset = elements(record.geometry || {}).find(child => child.nodeName === 'mxPoint' && attr(child, 'as') === 'offset');
      if (offset) geometry.labelOffset = { x: number(offset, 'x'), y: number(offset, 'y') };
      if (number(record.geometry, 'x') || number(record.geometry, 'y')) warn('连线文字的沿线相对位置已按编辑器规则居中，像素偏移已保留。');
      if (record.style.curved === '1') warn('曲线已转换为可编辑折线，原有控制点已保留。');
    }
    edges.push({ id: attr(record.cell, 'regionId', record.id), source: mappedIds.get(source), target: mappedIds.get(target), zIndex: layerIndex(record), dashed: record.style.dashed === '1', label: plainLabel(record.value, record.style.html === '1'), arrow: record.style.endArrow !== 'none', startArrow: Boolean(record.style.startArrow && record.style.startArrow !== 'none'), ...(hex(record.style.strokeColor) ? { strokeColor: record.style.strokeColor } : {}), ...geometry });
  });
  const name = attr(model, 'regionDocumentName', page.name).trim().slice(0, 60) || '导入的架构图';
  const state = normalizeGraph({ name, nodes, edges, selectedId: null });
  nodes.filter(node => node.type === 'container').forEach(node => {
    const normalized = state.nodes.find(item => item.id === node.id);
    if (normalized.x !== node.x || normalized.y !== node.y || normalized.w !== node.w || normalized.h !== node.h) warn('分组边界已适配编辑器的标题区与内边距，子节点的全局位置保持。');
  });
  return { state, pages: rawPages.map(({ id, name, index }) => ({ id, name, index })), warnings, selectedIndex: pageIndex };
}

export function exportDrawio(snapshot, { pageName = 'Page-1' } = {}) {
  const state = normalizeGraph(snapshot), nodeMap = new Map(state.nodes.map(node => [node.id, node]));
  const idMap = new Map(state.nodes.concat(state.edges).map(item => [item.id, `item-${escapeXml(item.id)}`]));
  const cells = ['<mxCell id="0"/>', '<mxCell id="1" parent="0"/>'];
  function nodeCell(node) {
    const parent = nodeMap.get(node.parentId);
    const { fill, stroke, font, strokeWidth, borderStyle, headerStyle, radius } = getNodeAppearance(node);
    const borderVisible = borderStyle !== 'none' && strokeWidth > 0;
    const appearance = Object.fromEntries(appearanceKeys.filter(key => Object.hasOwn(node, key)).map(key => [key, node[key]]));
    const dashes = borderStyle === 'dashed' ? 'dashed=1;dashPattern=3 3;' : borderStyle === 'dotted' ? 'dashed=1;dashPattern=1 2;' : 'dashed=0;';
    const image = node.iconAsset ? `shape=image;image=${imageStyleData(node.iconAsset.dataUrl)};imageAspect=1;` : '';
    const style = `${node.type === 'container' ? `swimlane;container=1;startSize=40;collapsible=0;swimlaneLine=${headerStyle === 'bar' && borderVisible ? 1 : 0};` : ''}${image}rounded=${radius > 0 ? 1 : 0};absoluteArcSize=1;arcSize=${radius * 2};strokeWidth=${strokeWidth};${dashes}whiteSpace=${node.overflow === 'wrap' ? 'wrap' : 'nowrap'};html=0;align=${['left', 'center', 'right'].includes(node.textAlign) ? node.textAlign : 'left'};verticalAlign=middle;fillColor=${fill};strokeColor=${borderVisible ? stroke : 'none'};fontColor=${font};`;
    const value = node.type === 'container' ? node.title : [node.title, node.description].filter(Boolean).join('\n');
    return `<mxCell id="${idMap.get(node.id)}" regionId="${escapeXml(node.id)}" regionZIndex="${node.zIndex}" regionAppearance="${escapeXml(JSON.stringify(appearance))}" value="${escapeXml(value)}" style="${escapeXml(style)}" vertex="1" parent="${parent ? idMap.get(parent.id) : '1'}" regionTitle="${escapeXml(node.title)}" regionDescription="${escapeXml(node.description)}" regionIcon="${escapeXml(node.icon)}"${node.iconAsset ? ` regionIconAsset="${escapeXml(JSON.stringify(node.iconAsset))}"` : ''} regionColor="${escapeXml(node.color)}"><mxGeometry x="${node.x - (parent?.x || 0)}" y="${node.y - (parent?.y || 0)}" width="${node.w}" height="${node.h}" as="geometry"/></mxCell>`;
  }
  function edgeCell(edge) {
    const geometry = Object.fromEntries(['sourceAnchor', 'targetAnchor', 'waypoints', 'labelOffset'].filter(key => edge[key] != null).map(key => [key, edge[key]]));
    const route = edge.sourceAnchor != null || edge.targetAnchor != null ? edgeGeometry(edge, state) : null;
    const terminals = [['exit', edge.sourceAnchor, edge.source, route?.points[0]], ['entry', edge.targetAnchor, edge.target, route?.points.at(-1)]].map(([prefix, anchor, nodeId, point]) => {
      if (!anchor) return '';
      anchor = anchorAt(nodeMap.get(nodeId), point);
      const [x, y] = anchor.side === 'left' ? [0, anchor.offset] : anchor.side === 'right' ? [1, anchor.offset] : anchor.side === 'top' ? [anchor.offset, 0] : [anchor.offset, 1];
      return `${prefix}X=${x};${prefix}Y=${y};${prefix}Dx=0;${prefix}Dy=0;${prefix}Perimeter=0;`;
    }).join('');
    const manual = Array.isArray(edge.waypoints);
    const style = `edgeStyle=${manual ? 'none' : 'orthogonalEdgeStyle'};rounded=0;html=0;strokeColor=${hex(edge.strokeColor) || '#64748b'};endArrow=${edge.arrow === false ? 'none' : 'block'};startArrow=${edge.startArrow ? 'block' : 'none'};dashed=${edge.dashed ? '1' : '0'};${terminals}`;
    const points = manual ? `<Array as="points">${edge.waypoints.map(point => `<mxPoint x="${point.x}" y="${point.y}"/>`).join('')}</Array>` : '';
    const offset = edge.labelOffset ? `<mxPoint x="${edge.labelOffset.x}" y="${edge.labelOffset.y}" as="offset"/>` : '';
    return `<mxCell id="${idMap.get(edge.id)}" regionId="${escapeXml(edge.id)}" regionZIndex="${edge.zIndex}"${Object.keys(geometry).length ? ` regionGeometry="${escapeXml(JSON.stringify(geometry))}"` : ''} value="${escapeXml(edge.label)}" style="${style}" edge="1" parent="1" source="${idMap.get(edge.source)}" target="${idMap.get(edge.target)}"><mxGeometry relative="1" as="geometry">${points}${offset}</mxGeometry></mxCell>`;
  }
  getPaintOrder(state).forEach(({ kind, item }) => cells.push(kind === 'node' ? nodeCell(item) : edgeCell(item)));
  const xml = `<?xml version="1.0" encoding="UTF-8"?><mxfile host="Architecture Studio"><diagram id="page-1" name="${escapeXml(pageName)}"><mxGraphModel regionDocumentName="${escapeXml(state.name)}"><root>${cells.join('')}</root></mxGraphModel></diagram></mxfile>`;
  if (encoder.encode(xml).length > LIMIT) throw new Error('draw.io 导出文件不能超过 2 MB，请减少图元或图标大小。');
  return xml;
}
